import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import DataTable from "@/components/DataTable";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Users, FileDown, FileUp, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { extractFromNid } from "@/lib/nid";
import { downloadStudentTemplate, parseStudentExcel } from "@/lib/excel";

const emptyForm = {
  stage: "", year: "", name: "", national_id: "", identity_number: "",
  birthdate: "", birth_place: "", gender: "ذكر", grade: "",
  class: "", section: "عربي", foreign_lang1: "", foreign_lang2: "",
  seat_number: "",
};

export default function Students() {
  const { ctx, meta } = useApp();
  const [rows, setRows] = useState([]);
  const [gradeFilter, setGradeFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(false);

  // Import state
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const grades = meta?.grades_by_stage?.[ctx.stage] || [];

  const load = () => {
    setLoading(true);
    api.get("/students", { params: {
      stage: ctx.stage, year: ctx.year,
      ...(gradeFilter ? { grade: gradeFilter } : {}),
    } })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [ctx.stage, ctx.year, gradeFilter]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, stage: ctx.stage, year: ctx.year, grade: grades[0] || "" });
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...emptyForm, ...row, class: row.class || "" });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = { ...form, stage: ctx.stage, year: ctx.year };
      if (editing) await api.put(`/students/${editing.id}`, payload);
      else await api.post("/students", payload);
      toast.success(editing ? "تم تحديث بيانات الطالب" : "تمت إضافة الطالب");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (row) => {
    if (!window.confirm(`حذف الطالب «${row.name}»؟`)) return;
    try { await api.delete(`/students/${row.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  /** Live-fill birthdate, birth_place, gender from a valid 14-digit NID. */
  const onNidChange = (v) => {
    const clean = String(v || "").replace(/\D/g, "").slice(0, 14);
    const parsed = extractFromNid(clean);
    setForm((prev) => {
      const next = { ...prev, national_id: clean };
      if (parsed) {
        next.birthdate = parsed.birthdate;
        next.birth_place = parsed.birth_place;
        next.gender = parsed.gender;
      }
      return next;
    });
  };

  const handleFilePick = () => fileInputRef.current?.click();

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ctx.stage || !ctx.year) {
      toast.error("اختر المرحلة والعام الدراسي أولاً");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const students = await parseStudentExcel(file);
      if (!students.length) {
        toast.error("الملف فارغ أو لا يحتوي على أعمدة معروفة");
        setImporting(false);
        e.target.value = "";
        return;
      }
      const { data } = await api.post("/students/bulk-import", {
        stage: ctx.stage, year: ctx.year, students,
      });
      setImportResult(data);
      setImportOpen(true);
      if (data.imported > 0) load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setImporting(false);
      e.target.value = "";  // reset for re-import of same file
    }
  };

  const columns = [
    { key: "seat_number", header: "رقم الجلوس", align: "end",
      render: (r) => <span className="font-medium tabular-nums">{r.seat_number || "—"}</span> },
    { key: "name", header: "اسم الطالب",
      render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "grade", header: "الصف" },
    { key: "class", header: "الفصل", render: (r) => r.class || "—" },
    { key: "section", header: "القسم",
      render: (r) => (
        <span className={"inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium " + (r.section === "لغات" ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-700")}>
          {r.section}
        </span>) },
    { key: "national_id", header: "الرقم القومي", render: (r) => <span className="text-muted-foreground text-xs tabular-nums">{r.national_id || "—"}</span> },
  ];

  return (
    <div className="space-y-6" data-testid="students-page">
      <PageHeader
        icon={Users}
        overline="الشؤون الأكاديمية"
        title="الطلاب"
        subtitle="إدارة كاملة لسجلات الطلاب، الاستيراد من Excel، والتعبئة التلقائية من الرقم القومي."
        badge={`${rows.length} طالب`}
        actions={
          <>
            <Select value={gradeFilter || "all"} onValueChange={(v) => setGradeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] h-10 bg-white/10 border-white/20 text-white hover:bg-white/15 rounded-lg" data-testid="students-grade-filter">
                <SelectValue placeholder="كل الصفوف" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الصفوف</SelectItem>
                {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={downloadStudentTemplate}
                     data-testid="students-template-btn"
                     className="h-10 bg-white/10 border-white/20 text-white hover:bg-white/15">
              <FileDown className="h-4 w-4 me-1.5" /> قالب Excel
            </Button>
            <Button variant="outline" onClick={handleFilePick}
                     disabled={importing}
                     className="h-10 bg-white/10 border-white/20 text-white hover:bg-white/15"
                     data-testid="students-import-btn">
              <FileUp className="h-4 w-4 me-1.5" />
              {importing ? "جارٍ الاستيراد…" : "استيراد Excel"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              hidden
              onChange={doImport}
              data-testid="students-import-file"
            />
            <Button
              onClick={openNew}
              data-testid="students-add-btn"
              className="h-10 bg-white text-[#0E7469] hover:bg-[#F4D06F] hover:text-[#083F38] font-semibold"
            >
              <Plus className="h-4 w-4 me-1.5" /> إضافة طالب
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        searchKeys={["name", "national_id", "seat_number", "class"]}
        actions={(row) => (
          <div className="flex items-center justify-end gap-1">
            <Button size="icon" variant="ghost" onClick={() => openEdit(row)} data-testid={`student-edit-${row.id}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(row)} data-testid={`student-del-${row.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
        emptyText={loading ? "جارٍ التحميل…" : "لا يوجد طلاب. ابدأ بإضافة طالب جديد."}
        testId="students-table"
      />

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <Users className="inline h-5 w-5 me-1.5" />
              {editing ? "تعديل بيانات طالب" : "إضافة طالب جديد"}
            </DialogTitle>
            <DialogDescription>
              أدخل بيانات الطالب. عند كتابة الرقم القومي (14 رقمًا) سيتم تعبئة تاريخ الميلاد ومحل الميلاد والنوع تلقائيًا.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="اسم الطالب" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="student-form-name" />
            <Field label="رقم الجلوس" value={form.seat_number} onChange={(v) => setForm({ ...form, seat_number: v })} testId="student-form-seat" />
            <SelectField label="الصف" value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })} options={grades} testId="student-form-grade" />
            <Field label="الفصل" value={form.class} onChange={(v) => setForm({ ...form, class: v })} testId="student-form-class" />
            <SelectField label="القسم" value={form.section} onValueChange={(v) => setForm({ ...form, section: v })} options={meta?.sections || []} testId="student-form-section" />
            <SelectField label="النوع" value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })} options={["ذكر", "أنثى"]} testId="student-form-gender" />
            <Field
              label="الرقم القومي (14 رقمًا)"
              value={form.national_id}
              onChange={onNidChange}
              testId="student-form-nid"
              dir="ltr"
              inputMode="numeric"
              maxLength={14}
              hint={form.national_id && form.national_id.length !== 14
                ? `يتبقى ${14 - form.national_id.length} أرقام`
                : (form.national_id.length === 14 ? "تم استخراج البيانات تلقائيًا" : "")}
            />
            <Field label="رقم الهوية / الإقامة" value={form.identity_number} onChange={(v) => setForm({ ...form, identity_number: v })} testId="student-form-identity" />
            <Field label="تاريخ الميلاد" type="date" value={form.birthdate} onChange={(v) => setForm({ ...form, birthdate: v })} testId="student-form-birthdate" />
            <Field label="محل الميلاد" value={form.birth_place} onChange={(v) => setForm({ ...form, birth_place: v })} testId="student-form-birthplace" />
            <Field label="اللغة الأولى" value={form.foreign_lang1} onChange={(v) => setForm({ ...form, foreign_lang1: v })} />
            <Field label="اللغة الثانية" value={form.foreign_lang2} onChange={(v) => setForm({ ...form, foreign_lang2: v })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="student-form-save">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import result dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg" dir="rtl" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <FileUp className="inline h-5 w-5 me-1.5" /> نتيجة الاستيراد
            </DialogTitle>
            <DialogDescription>
              إجمالي {importResult?.total ?? 0} صف — تم استيراد {importResult?.imported ?? 0} وتخطي {importResult?.skipped_count ?? 0}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-center gap-2 text-emerald-700 mb-1">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-[12px] font-medium">مستورَد بنجاح</span>
                </div>
                <div className="font-display text-3xl font-semibold text-emerald-800 tabular-nums" data-testid="import-imported-count">
                  {importResult?.imported ?? 0}
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center gap-2 text-amber-700 mb-1">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-[12px] font-medium">متجاهَل</span>
                </div>
                <div className="font-display text-3xl font-semibold text-amber-800 tabular-nums" data-testid="import-skipped-count">
                  {importResult?.skipped_count ?? 0}
                </div>
              </div>
            </div>

            {importResult?.skipped?.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 text-[12px] font-medium text-muted-foreground border-b border-border">
                  تفاصيل الصفوف المتجاهَلة
                </div>
                <div className="max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-3 py-2 text-start font-semibold text-muted-foreground">الصف</th>
                        <th className="px-3 py-2 text-start font-semibold text-muted-foreground">الاسم</th>
                        <th className="px-3 py-2 text-start font-semibold text-muted-foreground">السبب</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.skipped.map((s, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-1.5 tabular-nums">{s.row}</td>
                          <td className="px-3 py-1.5">{s.name || "—"}</td>
                          <td className="px-3 py-1.5 text-amber-700">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setImportOpen(false)} data-testid="import-result-close">تم</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, testId, hint, dir, inputMode, maxLength, type, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}{required && <span className="text-destructive ms-1">*</span>}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testId}
        dir={dir}
        inputMode={inputMode}
        maxLength={maxLength}
      />
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}
function SelectField({ label, value, onValueChange, options, testId }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
